import { useQuery } from "@tanstack/react-query";
import type { paths } from "@/openapi.gen.ts";
import { $api } from "@/utils/fetch.ts";

/** One model of the catalog, retired ones included. */
export type CatalogModelRow =
	paths["/api/models/catalog"]["get"]["responses"][200]["content"]["application/json"]["models"][number];

/**
 * Every model this deployment serves, plus the retired ones, for naming,
 * colouring and flagging a model anywhere in the app - including one a usage
 * row or an old chat still names. Which models a user may pick is
 * `chatModelsQueryOptions`.
 *
 * The list changes with a deployment, not within a session, so it is `static`:
 * the app-wide invalidation after every mutation (`main.tsx`) never refetches it.
 * Prefetched by the `_authenticated` layout.
 */
export const modelCatalogQueryOptions = () =>
	$api.queryOptions("get", "/api/models/catalog", {}, { staleTime: "static" });

const NO_MODELS: readonly CatalogModelRow[] = Object.freeze([]);

/**
 * The catalog, or an empty list until it has loaded. Deliberately `useQuery`,
 * not `useSuspenseQuery` (Rule 18): the catalog only names and flags models, so
 * a slow or failed request shows prettified ids instead of suspending or
 * breaking the page.
 */
export function useModelCatalog(): readonly CatalogModelRow[] {
	return useQuery(modelCatalogQueryOptions()).data?.models ?? NO_MODELS;
}
